import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ApplicationStatusIndicator from '../../../components/ui/ApplicationStatusIndicator';
import { supabase } from '../../../lib/supabase';
import DocumentViewerModal from './DocumentViewerModal';
import { sendRegistrationConfirmation } from '../../../services/emailService';


const ApplicationDetailsModal = ({ isOpen, onClose, application, onApprove, onReject }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedChassisNumber, setEditedChassisNumber] = useState(application?.chassisNumber || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Fetch status history when modal opens
  React.useEffect(() => {
    if (isOpen && application?.id) {
      fetchStatusHistory();
    }
  }, [isOpen, application?.id]);

  // Early return AFTER all hooks
  if (!isOpen || !application) return null;

  const fetchStatusHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { data, error } = await supabase?.from('application_status_history')?.select('*')?.eq('application_id', application?.id)?.order('changed_at', { ascending: true });

      if (error) throw error;
      setStatusHistory(data || []);
    } catch (error) {
      console.error('Error fetching status history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'unbestätigt': 'Unbestätigt',
      'bestätigt': 'Bestätigt',
      'processing': 'Beantragt',
      'approved': 'Genehmigt',
      'rejected': 'Abgelehnt',
      'redeemed': 'Eingelöst'
    };
    return labels?.[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'unbestätigt': 'AlertCircle',
      'bestätigt': 'CheckCircle',
      'processing': 'Clock',
      'approved': 'CheckCircle2',
      'rejected': 'XCircle',
      'redeemed': 'Gift'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusColor = (status) => {
    const colors = {
      'unbestätigt': 'text-yellow-600',
      'bestätigt': 'text-blue-600',
      'processing': 'text-blue-600',
      'approved': 'text-green-600',
      'rejected': 'text-red-600',
      'redeemed': 'text-purple-600'
    };
    return colors?.[status] || 'text-gray-600';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const handleViewDocument = () => {
    if (!application?.documentUrl) {
      alert('Keine Dokument-URL verfügbar');
      return;
    }
    setIsDocumentViewerOpen(true);
  };

  const handleDownloadDocument = async () => {
    if (!application?.documentUrl) {
      alert('Keine Dokument-URL verfügbar');
      return;
    }

    console.log('=== DOWNLOAD DIAGNOSTICS START ===');
    console.log('Application ID:', application?.id);
    console.log('Customer Name:', application?.customerName);
    console.log('Original Document URL:', application?.documentUrl);

    try {
      // Extract file path from storage URL - improved version
      const extractFilePath = (url) => {
        if (!url) {
          console.error('extractFilePath: No URL provided');
          return null;
        }
        
        console.log('extractFilePath: Processing URL:', url);
        
        // Remove any query parameters and fragments first
        const cleanUrl = url?.split('?')?.[0]?.split('#')?.[0];
        console.log('extractFilePath: Cleaned URL:', cleanUrl);
        
        // Pattern 1: Full Supabase storage URL
        if (cleanUrl?.includes('/storage/v1/object/')) {
          console.log('extractFilePath: Attempting Pattern 1 (Full Supabase URL)');
          const parts = cleanUrl?.split('/storage/v1/object/');
          if (parts?.length > 1) {
            let path = parts?.[1]?.replace(/^(public|authenticated)\//, '');
            path = path?.replace(/^vehicle-documents\//, '');
            console.log('extractFilePath: Pattern 1 SUCCESS - Path:', path);
            return path;
          }
          console.warn('extractFilePath: Pattern 1 FAILED - Parts length:', parts?.length);
        }
        
        // Pattern 2: Contains 'vehicle-documents/' anywhere
        if (cleanUrl?.includes('vehicle-documents/')) {
          console.log('extractFilePath: Attempting Pattern 2 (vehicle-documents/ pattern)');
          const index = cleanUrl?.indexOf('vehicle-documents/');
          let path = cleanUrl?.substring(index + 'vehicle-documents/'?.length);
          console.log('extractFilePath: Pattern 2 SUCCESS - Path:', path);
          return path;
        }
        
        // Pattern 3: Already a clean path
        if (!cleanUrl?.includes('http') && !cleanUrl?.includes('supabase')) {
          console.log('extractFilePath: Pattern 3 - Already clean path:', cleanUrl);
          return cleanUrl;
        }
        
        console.error('extractFilePath: ALL PATTERNS FAILED');
        return null;
      };

      const filePath = extractFilePath(application?.documentUrl);
      if (!filePath) {
        throw new Error('Ungültige Dokument-URL. Pfad konnte nicht extrahiert werden.');
      }

      console.log('✅ File path extracted successfully:', filePath);
      console.log('Attempting download from bucket: vehicle-documents');

      // Download file from storage
      const { data, error } = await supabase?.storage?.from('vehicle-documents')?.download(filePath);
      
      if (error) {
        console.error('❌ Storage download error:', {
          message: error?.message,
          statusCode: error?.statusCode,
          error: error
        });
        throw new Error(error.message || 'Fehler beim Laden des Dokuments');
      }
      
      if (!data) {
        console.error('❌ No data received from storage');
        throw new Error('Keine Daten erhalten');
      }
      
      console.log('✅ Document downloaded successfully');
      console.log('Blob size:', data?.size, 'bytes');
      console.log('Blob type:', data?.type);
      
      // Determine file extension
      const fileName = filePath?.split('/')?.pop();
      const extension = fileName?.includes('.') ? fileName?.split('.')?.pop() : 'pdf';
      
      // Create download filename
      const downloadName = `${application?.customerName?.replace(/\s+/g, '_')}_dokument.${extension}`;
      console.log('Download filename:', downloadName);
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(data);
      console.log('Blob URL created:', blobUrl);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      
      // Clean up blob URL
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        console.log('Blob URL revoked');
      }, 1000);
      
      console.log('✅ Download triggered successfully');
      console.log('=== DOWNLOAD DIAGNOSTICS END (SUCCESS) ===');
    } catch (error) {
      console.error('=== DOWNLOAD DIAGNOSTICS END (FAILED) ===');
      console.error('Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      alert(`Fehler beim Herunterladen: ${error?.message}\n\nBitte siehe Browser-Konsole (F12) für detaillierte Diagnose-Informationen.`);
    }
  };

  const handleSaveChassisNumber = async () => {
    if (!editedChassisNumber || editedChassisNumber?.length !== 17) {
      setSaveError('Die Fahrgestellnummer muss genau 17 Zeichen lang sein');
      return;
    }

    const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinPattern?.test(editedChassisNumber)) {
      setSaveError('Ungültiges Format. Die FIN darf die Buchstaben I, O, Q nicht enthalten.');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      const { error } = await supabase
        ?.from('applications')
        ?.update({ chassis_number: editedChassisNumber })
        ?.eq('id', application?.id);

      if (error) throw error;

      // Update local application object
      application.chassisNumber = editedChassisNumber;
      setIsEditing(false);
      
      // Refresh the page to show updated data
      window.location?.reload();
    } catch (error) {
      console.error('Error saving chassis number:', error);
      setSaveError(error?.message || 'Fehler beim Speichern der Fahrgestellnummer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResendConfirmation = async () => {
    setIsResendingConfirmation(true);
    setResendSuccess(false);
    
    try {
      // Fetch full application data with dealership info
      const { data: appData, error: fetchError } = await supabase
        ?.from('applications')
        ?.select(`
          *,
          dealerships (
            name
          )
        `)
        ?.eq('id', application?.id)
        ?.single();
      
      if (fetchError) throw fetchError;
      
      // Generate new confirmation token if not exists
      let confirmationToken = appData?.confirmation_token;
      if (!confirmationToken) {
        confirmationToken = `${Date.now()}-${Math.random()?.toString(36)?.substring(2, 15)}`;
        const { error: updateError } = await supabase
          ?.from('applications')
          ?.update({ 
            confirmation_token: confirmationToken,
            confirmation_sent_at: new Date()?.toISOString()
          })
          ?.eq('id', application?.id);
        
        if (updateError) throw updateError;
      } else {
        // Update confirmation_sent_at timestamp
        const { error: updateError } = await supabase
          ?.from('applications')
          ?.update({ confirmation_sent_at: new Date()?.toISOString() })
          ?.eq('id', application?.id);
        
        if (updateError) throw updateError;
      }
      
      // Send confirmation email
      const result = await sendRegistrationConfirmation({
        customerName: appData?.customer_name,
        customerEmail: appData?.customer_email,
        dealershipName: appData?.dealerships?.name,
        confirmationToken: confirmationToken,
        applicationDate: appData?.application_date
      });
      
      if (!result?.success) {
        throw new Error(result?.error || 'Fehler beim Senden der E-Mail');
      }
      
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      console.error('Error resending confirmation:', error);
      alert(`Fehler beim Senden der Bestätigungs-E-Mail: ${error?.message}`);
    } finally {
      setIsResendingConfirmation(false);
    }
  };

  const detailSections = [
    {
      title: 'Kundeninformationen',
      icon: 'User',
      fields: [
        { label: 'Name', value: application?.customerName || 'Nicht angegeben' },
        { label: 'E-Mail', value: application?.email || 'Nicht angegeben' },
        { label: 'Telefon', value: application?.phone || 'Nicht angegeben' }
      ]
    },
    {
      title: 'Fahrzeuginformationen',
      icon: 'Car',
      fields: [
        { label: 'Modell', value: application?.vehicleModel || 'Nicht angegeben' }
      ]
    },
    {
      title: 'Antragsinformationen',
      icon: 'FileText',
      fields: [
        { label: 'Partner', value: application?.dealershipName || 'Nicht angegeben' },
        { label: 'Antragsdatum', value: application?.applicationDate || 'Nicht angegeben' },
        { label: 'Status', value: application?.status || 'Nicht angegeben' },
        { label: 'Prämie', value: application?.premiumAmount ? `€${application?.premiumAmount}` : 'Nicht angegeben' }
      ]
    }
  ];

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
        <div className="bg-card rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="FileText" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-semibold text-foreground">
                  Antragsdetails
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <ApplicationStatusIndicator status={application?.status} size="sm" />
                  <span className="text-sm text-muted-foreground">
                    ID: {application?.id}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Schließen"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {application?.documentUrl && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Icon name="FileText" size={20} color="var(--color-blue-500)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Fahrzeugschein hochgeladen</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Dokument verfügbar zum Anzeigen und Herunterladen</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={handleViewDocument}
                    >
                      Anzeigen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={handleDownloadDocument}
                    >
                      Herunterladen
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Registration Confirmation Resend Section */}
            {application?.status === 'unbestätigt' && (
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                      <Icon name="Mail" size={20} color="var(--color-amber-500)" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">Registrierungsbestätigung</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        E-Mail-Bestätigung erneut an Kunden senden
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {resendSuccess && (
                      <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                        <Icon name="CheckCircle2" size={14} />
                        Gesendet
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Send"
                      iconPosition="left"
                      onClick={handleResendConfirmation}
                      loading={isResendingConfirmation}
                      disabled={isResendingConfirmation}
                    >
                      Bestätigung senden
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {detailSections?.map((section) => (
              <div key={section?.title} className="bg-muted/30 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name={section?.icon} size={20} color="var(--color-primary)" />
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    {section?.title}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {section?.fields?.map((field) => (
                    <div key={field?.label}>
                      <p className="text-xs text-muted-foreground mb-1">{field?.label}</p>
                      <p className="text-sm font-medium text-foreground">{field?.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Chassis Number Section - Editable by Admin */}
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="Hash" size={20} color="var(--color-blue-500)" />
                  <h3 className="text-lg font-heading font-semibold text-foreground">
                    Fahrgestellnummer (FIN)
                  </h3>
                </div>
                {!isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    iconPosition="left"
                    onClick={() => setIsEditing(true)}
                  >
                    Bearbeiten
                  </Button>
                )}
              </div>
              
              {isEditing ? (
                <div className="space-y-3">
                  <Input
                    label="Fahrgestellnummer aus Feld E des Fahrzeugscheins"
                    type="text"
                    value={editedChassisNumber}
                    onChange={(e) => {
                      setEditedChassisNumber(e?.target?.value?.toUpperCase());
                      setSaveError('');
                    }}
                    placeholder="17-stellige FIN eingeben"
                    maxLength={17}
                    error={saveError}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground">
                    Die FIN muss genau 17 Zeichen lang sein und darf die Buchstaben I, O, Q nicht enthalten.
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Save"
                      iconPosition="left"
                      onClick={handleSaveChassisNumber}
                      loading={isSaving}
                      disabled={!editedChassisNumber || editedChassisNumber?.length !== 17}
                    >
                      Speichern
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="X"
                      onClick={() => {
                        setIsEditing(false);
                        setEditedChassisNumber(application?.chassisNumber || '');
                        setSaveError('');
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white/50 rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-1">Fahrgestellnummer (Feld E)</p>
                  {application?.chassisNumber ? (
                    <p className="text-base font-mono font-bold text-foreground">{application?.chassisNumber}</p>
                  ) : (
                    <p className="text-sm text-warning flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} />
                      Noch nicht erfasst - bitte aus Fahrzeugschein übertragen
                    </p>
                  )}
                </div>
              )}
            </div>

            {application?.notes && (
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">Notizen</p>
                    <p className="text-sm text-muted-foreground">{application?.notes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Status Transition History Section */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <Icon name="GitBranch" size={20} color="#3b82f6" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  Status-Verlauf
                </h3>
              </div>

              {isLoadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : statusHistory?.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-border"></div>

                  {/* Timeline items */}
                  <div className="space-y-6">
                    {statusHistory?.map((history, index) => (
                      <div key={history?.id} className="relative flex gap-4">
                        {/* Timeline dot */}
                        <div className={`relative z-10 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                          index === statusHistory?.length - 1 ? 'bg-primary' : 'bg-muted'
                        }`}>
                          <Icon 
                            name={getStatusIcon(history?.to_status)} 
                            size={20} 
                            color={index === statusHistory?.length - 1 ? '#ffffff' : 'var(--color-muted-foreground)'} 
                          />
                        </div>

                        {/* Timeline content */}
                        <div className="flex-1 pb-6">
                          <div className={`p-4 rounded-lg border ${
                            index === statusHistory?.length - 1 ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-border'
                          }`}>
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex items-center gap-2">
                                {history?.from_status && (
                                  <>
                                    <span className={`text-sm font-medium ${getStatusColor(history?.from_status)}`}>
                                      {getStatusLabel(history?.from_status)}
                                    </span>
                                    <Icon name="ArrowRight" size={16} color="var(--color-muted-foreground)" />
                                  </>
                                )}
                                <span className={`text-sm font-semibold ${getStatusColor(history?.to_status)}`}>
                                  {getStatusLabel(history?.to_status)}
                                </span>
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateTime(history?.changed_at)}
                              </span>
                            </div>

                            <div className="space-y-1">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                                <span>
                                  {history?.changed_by_email || 'System'}
                                </span>
                              </div>

                              {history?.notes && (
                                <div className="flex items-start gap-2 text-sm text-foreground mt-2">
                                  <Icon name="FileText" size={14} color="var(--color-muted-foreground)" className="mt-0.5" />
                                  <span className="flex-1">{history?.notes}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Icon name="GitBranch" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-50" />
                  <p>Kein Status-Verlauf verfügbar</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            {application?.status === 'applied' && (
              <>
                <Button
                  variant="danger"
                  iconName="XCircle"
                  iconPosition="left"
                  onClick={() => {
                    onReject(application?.id);
                    onClose();
                  }}
                >
                  Ablehnen
                </Button>
                <Button
                  variant="success"
                  iconName="CheckCircle2"
                  iconPosition="left"
                  onClick={() => {
                    onApprove(application?.id);
                    onClose();
                  }}
                >
                  Genehmigen
                </Button>
              </>
            )}
            <Button variant="outline" onClick={onClose} iconName="">
              Schließen
            </Button>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <DocumentViewerModal
          isOpen={isDocumentViewerOpen}
          onClose={() => setIsDocumentViewerOpen(false)}
          application={application}
          onUpdateChassisNumber={async (appId, newChassisNumber) => {
            try {
              const { error } = await supabase
                ?.from('applications')
                ?.update({ chassis_number: newChassisNumber })
                ?.eq('id', appId);
              
              if (error) throw error;
              
              // Update local application object
              application.chassisNumber = newChassisNumber;
              
              // Refresh to show updated data
              window.location?.reload();
            } catch (error) {
              console.error('Error updating chassis number:', error);
              alert(`Fehler beim Speichern: ${error?.message}`);
            }
          }}
        />
      )}
    </>
  );
};

export default ApplicationDetailsModal;